import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import axios from 'axios'
import { Download, FileText, Clock, CheckCircle, XCircle, Loader } from 'lucide-react'
import moment from 'moment'

import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../../ui/table'
import { Badge } from '../../ui/badge'
import { Button } from '../../ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import LoadingSpinner from '../../leevo_ui/LoadingSpinner/LoadingSpinner'

function ReportHistory({ apiBasePath }) {
  const [reports, setReports] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [filterType, setFilterType] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState(null)
  const { replace: redirect } = useHistory()

  useEffect(() => {
    loadReports()
    const interval = setInterval(loadReports, 10000)
    return () => {
      clearInterval(interval)
    }
  }, [filterType, filterStatus, currentPage, apiBasePath])

  async function loadReports() {
    try {
      const params = { page: currentPage }
      if (filterType !== 'all') params.report_type = filterType
      if (filterStatus !== 'all') params.status = filterStatus

      const response = await axios.get(apiBasePath, { params })
      setReports(response.data.reports || [])
      setPagination(response.data.pagination)
      setIsLoading(false)
    } catch (error) {
      if (error.response?.status === 401) {
        redirect('/login')
      } else {
        setReports([])
        setIsLoading(false)
      }
    }
  }

  function handleDownload(report) {
    if (report.status === 'completed' && report.file_url) {
      window.open(report.file_url, '_blank')
    }
  }

  function getStatusIcon(status) {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'processing':
        return <Loader className="h-4 w-4 text-blue-600 animate-spin" />
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />
      default:
        return <Clock className="h-4 w-4 text-gray-400" />
    }
  }

  function getStatusBadge(status) {
    const variants = {
      completed: 'default',
      processing: 'secondary',
      failed: 'destructive',
      pending: 'outline'
    }

    return (
      <Badge variant={variants[status]} className="capitalize">
        {status}
      </Badge>
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Report History</h2>
        <p className="text-gray-600">View and download your previously generated reports</p>
      </div>

      <div className="flex space-x-4">
        <div className="w-48">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger>
              <SelectValue placeholder="Report Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="lts_registration">LTS Registration</SelectItem>
              <SelectItem value="lts_monthly">Monthly LTS</SelectItem>
              <SelectItem value="enrollment">Enrollment</SelectItem>
              <SelectItem value="daily_class_roster">All Classes by Day</SelectItem>
              <SelectItem value="klass_package_roster">Single Class Roster</SelectItem>
              <SelectItem value="klass_package_registration">Class Package Registration</SelectItem>
              <SelectItem value="skill_progress">Skill Progress</SelectItem>
              <SelectItem value="staff_coach">Staff & Coaches</SelectItem>
              <SelectItem value="financial_transactions">Financial Transactions</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="w-48">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {reports.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No reports found</p>
          <p className="text-sm text-gray-500 mt-1">Generate your first report to see it here</p>
        </div>
      ) : (
        <>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Parameters</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Format</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{report.display_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600">{report.parameters_summary || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(report.status)}
                        {getStatusBadge(report.status)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-mono uppercase">{report.file_format}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600">
                        {moment(report.created_at).format('MMM D, YYYY h:mm A')}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {report.status === 'completed' && report.file_url ? (
                        <Button size="sm" variant="outline" onClick={() => handleDownload(report)}>
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {pagination && pagination.total_pages > 1 && (
            <div className="flex justify-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="px-4 py-2 text-sm">
                Page {currentPage} of {pagination.total_pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(pagination.total_pages, p + 1))}
                disabled={currentPage === pagination.total_pages}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

ReportHistory.propTypes = {
  apiBasePath: PropTypes.string.isRequired
}

export default ReportHistory
